import { useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faSpinner,
  faExclamationTriangle,
  faPlus,
  faPlay,
  faArrowRightArrowLeft,
  faUserPlus,
  faBolt,
  faHistory,
  faChevronDown,
  faChevronUp,
  faXmark,
  faClipboardList,
  faClipboardCheck,
  faMagnifyingGlass,
  faUserCheck,
  faFileInvoiceDollar,
  faFileSignature,
  faRightLeft,
  faClockRotateLeft,
  faFileLines,
  faListCheck,
  faFilePdf,
  faDownload,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  inspectionService,
  INSPECTION_STATUS_LABELS,
  WARRANTY_STATUS_LABELS,
  OPS_REVIEW_STATUS_LABELS,
  COMMERCIAL_STATUS_LABELS,
  CUSTOMER_APPROVAL_LABELS,
  WORK_ORDER_REVIEW_LABELS,
  type InspectionStatus,
  type InspectionFindingSeverity,
  type OperationsReviewStatus,
  type CommercialReviewStatus,
  type CustomerApprovalStatus,
  type Finding,
} from '@/services/inspection.service';
import { SectionShell, GroupLabel } from './components/SectionShell';
import { faComments } from '@fortawesome/free-solid-svg-icons';
import { ModificationPanel } from './components/modification/ModificationPanel';
import { InternalNotesSection } from './components/InternalNotesSection';
import { InspectionReportSection } from './components/report/InspectionReportSection';
import { FindingAccordionItem } from './components/FindingAccordionItem';
import { ProcedureExecutionPanel } from './components/ProcedureExecutionPanel';
import { ConditionChecksSection } from './components/ConditionChecksSection';
import { SkeletonPage } from '@/components/skeletons';
import { InspectionElapsedIndicator } from './components/InspectionElapsedIndicator';
import { HeaderActionsMenu } from './components/HeaderActionsMenu';
import { Modal } from '@/components/shared/Modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { FormField } from '@/components/shared/FormField';
import { toast, swalConfirm } from '@/lib/swal';
import { useHasPermission } from '@/hooks/use-permissions';
import { useAuthStore } from '@/stores/auth.store';
import { TransferDialog } from './components/ownership/TransferDialog';
import { TakeoverDialog } from './components/ownership/TakeoverDialog';
import { ForceTransferDialog } from './components/ownership/ForceTransferDialog';
import { PendingOwnershipRequests } from './components/ownership/PendingOwnershipRequests';
import { OwnershipHistoryPanel } from './components/ownership/OwnershipHistoryPanel';
import { ElapsedTimer } from './components/ownership/ElapsedTimer';
import {
  isPdfFile,
  isWorkOrderAwaitingAcceptance,
  quotationSendGate,
  staffCustomerDecisionActions,
} from './quotation-send-rules';

// ── Helpers ───────────────────────────────────────────────────────────────────

function extractMsg(error: Error, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

function fmtDate(v: string | null | undefined): string {
  if (!v) return '—';
  return new Date(v).toLocaleDateString();
}

function fmtDateTime(v: string | null | undefined): string {
  if (!v) return '—';
  return new Date(v).toLocaleString();
}

// ── Badge maps ────────────────────────────────────────────────────────────────

// Status color system (UI/UX refinement PART 4): only four semantic colors —
// Success = green, Warning/awaiting = amber, Pending/not-started = blue,
// Critical/negative = red. No other hues are introduced.
const BADGE = {
  green: 'bg-green-500/10 text-green-700',
  amber: 'bg-amber-500/10 text-amber-700',
  blue: 'bg-blue-500/10 text-blue-700',
  red: 'bg-red-500/10 text-red-700',
} as const;

const STATUS_BADGE: Record<InspectionStatus, string> = {
  UNASSIGNED: BADGE.blue,
  SCHEDULED: BADGE.blue,
  ASSIGNED: BADGE.blue,
  IN_PROGRESS: BADGE.amber,
  TRANSFER_REQUESTED: BADGE.amber,
  COMPLETED: BADGE.green,
  REVIEWED: BADGE.green,
  CLOSED: BADGE.green,
  CANCELLED: BADGE.red,
};

const OPS_BADGE: Record<OperationsReviewStatus, string> = {
  PENDING: BADGE.blue,
  RETURNED: BADGE.red,
  REVIEWED: BADGE.amber,
  APPROVED_FOR_COMMERCIAL: BADGE.green,
};

const COMMERCIAL_BADGE: Record<CommercialReviewStatus, string> = {
  PENDING: BADGE.blue,
  SENT_TO_CUSTOMER: BADGE.amber,
  RETURNED_TO_OPERATIONS: BADGE.red,
};

const CUSTOMER_BADGE: Record<CustomerApprovalStatus, string> = {
  PENDING: BADGE.blue,
  APPROVED: BADGE.green,
  REJECTED: BADGE.red,
  MODIFICATION_REQUESTED: BADGE.amber,
};

// ── Shared sub-components ─────────────────────────────────────────────────────

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm text-foreground">{value ?? '—'}</span>
    </div>
  );
}

function SectionCard({
  icon,
  title,
  children,
  actions,
}: {
  icon: IconDefinition;
  title: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
}) {
  return (
    <SectionShell icon={icon} title={title} actions={actions}>
      {children}
    </SectionShell>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function InspectionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const can = useHasPermission();
  // B6.19 — cancel requires a reason, mirroring the OPS return dialog.
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const currentUserId = useAuthStore((s) => s.user?.userId ?? '');

  // Modal state: Ops Return
  const [opsReturnOpen, setOpsReturnOpen] = useState(false);
  const [opsReturnReason, setOpsReturnReason] = useState('');

  // Modal state: Finding CRUD
  const [findingModalOpen, setFindingModalOpen] = useState(false);
  const [editFinding, setEditFinding] = useState<Finding | null>(null);
  const [findingForm, setFindingForm] = useState({
    severity: 'MEDIUM' as InspectionFindingSeverity,
    title: '',
    description: '',
    componentArea: '',
    insideRequestedScope: false,
    warrantyApplicable: false,
    recommendedAction: '',
    recommendedPartsSummary: '',
    estimatedRepairHours: '',
  });

  // Track the most recently created finding so it auto-expands
  const [latestCreatedFindingId, setLatestCreatedFindingId] = useState<string | null>(null);

  // Modal state: Commercial Send
  const [commSendOpen, setCommSendOpen] = useState(false);
  const [commSendSummary, setCommSendSummary] = useState('');
  const [commSendRef, setCommSendRef] = useState('');

  // Modal state: Commercial Return
  const [commReturnOpen, setCommReturnOpen] = useState(false);
  const [commReturnReason, setCommReturnReason] = useState('');

  // Modal state: Customer Modification Request
  const [custModOpen, setCustModOpen] = useState(false);
  const [custModReason, setCustModReason] = useState('');
  // D-129 — quotation PDF picker (Send modal) and the staff-recorded approval
  // with the customer's Work Order.
  const quotationDocInputRef = useRef<HTMLInputElement>(null);
  const [custApproveOpen, setCustApproveOpen] = useState(false);
  const [custApproveNumber, setCustApproveNumber] = useState('');
  const [custApproveDescription, setCustApproveDescription] = useState('');
  const [custApproveFile, setCustApproveFile] = useState<File | null>(null);
  const resetCustApprove = () => {
    setCustApproveOpen(false);
    setCustApproveNumber('');
    setCustApproveDescription('');
    setCustApproveFile(null);
  };

  // Modal state: Ownership actions
  const [transferOpen, setTransferOpen] = useState(false);
  const [takeoverOpen, setTakeoverOpen] = useState(false);
  const [forceTransferOpen, setForceTransferOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  // ── Queries ────────────────────────────────────────────────────────────────

  const { data: inspection, isLoading } = useQuery({
    queryKey: ['inspection', id],
    queryFn: () => inspectionService.get(id!),
    enabled: !!id,
  });

  const { data: findingsData, isLoading: findingsLoading } = useQuery({
    queryKey: ['inspection-findings', id],
    queryFn: () => inspectionService.getFindings(id!, { pageSize: 100 }),
    enabled: !!id,
  });

  // B6.19 — the backend decides whether Close may be offered. The UI never
  // infers eligibility from status: an APPROVED Service Report is now a second,
  // independent route (DEC-2), and duplicating that rule here is exactly how the
  // button and the API drift apart.
  const { data: closure } = useQuery({
    queryKey: ['inspection-closure-eligibility', id],
    queryFn: () => inspectionService.closureEligibility(id!),
    enabled: !!id,
  });

  // D-129 — AME-signed download links for the quotation / Work Order PDFs
  // (only for holders of inspection:download-commercial-pdf; the backend
  // returns null URLs otherwise, so the query is simply not issued).
  const { data: commercialLinks } = useQuery({
    queryKey: ['inspection-commercial-links', id],
    queryFn: () => inspectionService.getCommercialDocumentLinks(id!),
    enabled:
      !!id &&
      !!inspection &&
      can('inspection:download-commercial-pdf') &&
      (!!inspection.quotationDocumentId || !!inspection.workOrderDocumentId),
  });
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['inspection', id] });
    void qc.invalidateQueries({ queryKey: ['inspection-closure-eligibility', id] });
    void qc.invalidateQueries({ queryKey: ['inspection-commercial-links', id] });
    // The OPS dashboard's terminal-state KPIs and lifecycle chart change too.
    void qc.invalidateQueries({ queryKey: ['dashboard-operations'] });
    // Report readiness is derived from inspection-level inputs (complaint,
    // customer, equipment, findings). Keep the readiness gate in sync whenever
    // any of them change so the banner/counter never shows stale requirements.
    void qc.invalidateQueries({ queryKey: ['report-readiness', id] });
  };

  // ── Mutations ──────────────────────────────────────────────────────────────

  const submitMutation = useMutation({
    mutationFn: () => inspectionService.submit(id!),
    onSuccess: () => {
      toast.success('Inspection submitted for operations review');
      invalidate();
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to submit inspection')),
  });

  // ── B6.19 — lifecycle mutations ────────────────────────────────────────────
  const closeMutation = useMutation({
    mutationFn: () => inspectionService.close(id!),
    onSuccess: () => {
      toast.success('Job closed');
      invalidate();
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to close job')),
  });

  const cancelMutation = useMutation({
    mutationFn: (reason: string) => inspectionService.cancel(id!, reason),
    onSuccess: () => {
      toast.success('Job cancelled');
      setCancelOpen(false);
      setCancelReason('');
      invalidate();
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to cancel job')),
  });

  const ackReturnMutation = useMutation({
    mutationFn: () => inspectionService.acknowledgeCommercialReturn(id!),
    onSuccess: () => {
      toast.success('Returned to the Operations review queue');
      invalidate();
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to acknowledge return')),
  });

  const opsApproveMutation = useMutation({
    mutationFn: () => inspectionService.operationsApprove(id!),
    onSuccess: () => {
      toast.success('Approved for commercial');
      invalidate();
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to approve')),
  });

  const opsReturnMutation = useMutation({
    mutationFn: (reason: string) => inspectionService.operationsReturn(id!, reason),
    onSuccess: () => {
      toast.success('Returned to engineer');
      setOpsReturnOpen(false);
      setOpsReturnReason('');
      invalidate();
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to return inspection')),
  });

  const commSendMutation = useMutation({
    mutationFn: (payload: { commercialSummary: string; quotationReference: string }) =>
      inspectionService.commercialSend(id!, payload),
    onSuccess: () => {
      toast.success('Sent to customer');
      setCommSendOpen(false);
      setCommSendSummary('');
      setCommSendRef('');
      invalidate();
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to send to customer')),
  });

  const commReturnMutation = useMutation({
    mutationFn: (reason: string) => inspectionService.commercialReturn(id!, reason),
    onSuccess: () => {
      toast.success('Returned to operations');
      setCommReturnOpen(false);
      setCommReturnReason('');
      invalidate();
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to return to operations')),
  });

  // D-129 — staff-recorded approval carries the customer's Work Order.
  const custApproveMutation = useMutation({
    mutationFn: (payload: { workOrderNumber: string; workOrderDescription: string; file: File }) =>
      inspectionService.customerApprove(id!, payload),
    onSuccess: () => {
      toast.success('Customer approval and Work Order recorded');
      resetCustApprove();
      invalidate();
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to record the approval')),
  });
  // D-129 — quotation PDF gate: attach / replace / remove before sending.
  const attachQuotationDocMutation = useMutation({
    mutationFn: (file: File) => inspectionService.attachQuotationDocument(id!, file),
    onSuccess: () => {
      toast.success('Quotation PDF attached');
      invalidate();
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to attach the quotation PDF')),
  });
  const removeQuotationDocMutation = useMutation({
    mutationFn: () => inspectionService.removeQuotationDocument(id!),
    onSuccess: () => {
      toast.success('Quotation PDF removed');
      invalidate();
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to remove the quotation PDF')),
  });
  // D-129 — Commercial accepts the customer-submitted Work Order.
  const acceptWorkOrderMutation = useMutation({
    mutationFn: () => inspectionService.acceptWorkOrder(id!),
    onSuccess: () => {
      toast.success('Work Order accepted — the job can now proceed');
      invalidate();
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to accept the Work Order')),
  });

  const custRejectMutation = useMutation({
    mutationFn: () => inspectionService.customerReject(id!),
    onSuccess: () => {
      toast.success('Quotation rejected');
      invalidate();
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to reject')),
  });

  const custModMutation = useMutation({
    mutationFn: (reason: string) => inspectionService.customerRequestModification(id!, reason),
    onSuccess: () => {
      toast.success('Modification requested');
      setCustModOpen(false);
      setCustModReason('');
      invalidate();
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to request modification')),
  });

  const startMutation = useMutation({
    mutationFn: () => inspectionService.start(id!),
    onSuccess: () => {
      toast.success('Inspection started');
      invalidate();
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to start inspection')),
  });

  const createFindingMutation = useMutation({
    mutationFn: (payload: Parameters<typeof inspectionService.createFinding>[1]) =>
      inspectionService.createFinding(id!, payload),
    onSuccess: (created) => {
      toast.success('Finding added');
      setFindingModalOpen(false);
      setLatestCreatedFindingId(created.id);
      void qc.invalidateQueries({ queryKey: ['inspection-findings', id] });
      invalidate();
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to add finding')),
  });

  const updateFindingMutation = useMutation({
    mutationFn: ({ fId, payload }: { fId: string; payload: Partial<typeof findingForm> }) =>
      inspectionService.updateFinding(id!, fId, payload),
    onSuccess: () => {
      toast.success('Finding updated');
      setFindingModalOpen(false);
      setEditFinding(null);
      void qc.invalidateQueries({ queryKey: ['inspection-findings', id] });
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to update finding')),
  });

  const deleteFindingMutation = useMutation({
    mutationFn: (fId: string) => inspectionService.deleteFinding(id!, fId),
    onSuccess: () => {
      toast.success('Finding deleted');
      void qc.invalidateQueries({ queryKey: ['inspection-findings', id] });
      invalidate();
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to delete finding')),
  });

  const revokeTransferMutation = useMutation({
    mutationFn: () => inspectionService.revokeTransfer(id!),
    onSuccess: () => {
      toast.success('Transfer revoked');
      invalidate();
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to revoke transfer')),
  });

  const claimMutation = useMutation({
    mutationFn: () => inspectionService.claim(id!),
    onSuccess: () => {
      toast.success('Inspection claimed');
      invalidate();
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to claim inspection')),
  });

  // ── Action handlers ────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    const ok = await swalConfirm({
      title: 'Submit Inspection?',
      text: 'This will submit the inspection for operations review.',
      confirmText: 'Submit',
    });
    if (ok) submitMutation.mutate();
  };

  const handleClose = async () => {
    const ok = await swalConfirm({
      title: 'Close this job?',
      text:
        'Closing marks the job finished and is permanent — a closed job cannot be reopened, edited or cancelled. ' +
        'The Service Report and its approved revisions are not affected.',
      confirmText: 'Close Job',
    });
    if (ok) closeMutation.mutate();
  };

  const handleOpsApprove = async () => {
    const ok = await swalConfirm({
      title: 'Approve for Commercial?',
      text: 'This will mark the inspection as approved and move it to commercial review.',
      confirmText: 'Approve',
    });
    if (ok) opsApproveMutation.mutate();
  };

  // D-129 — the approval needs the customer's Work Order, so it opens a form
  // rather than a bare confirm.
  const handleCustApprove = () => setCustApproveOpen(true);
  const handleAcceptWorkOrder = async () => {
    const ok = await swalConfirm({
      title: 'Accept Work Order?',
      text: `Accepting Work Order ${inspection?.workOrderNumber ?? ''} confirms the customer's approval and lets Operations continue the job.`,
      confirmText: 'Accept Work Order',
    });
    if (ok) acceptWorkOrderMutation.mutate();
  };
  const handleQuotationDocPicked = (file: File | null) => {
    if (!file) return;
    if (!isPdfFile(file)) {
      toast.error('Only PDF files are accepted for the quotation document.');
      return;
    }
    attachQuotationDocMutation.mutate(file);
  };

  const handleCustReject = async () => {
    const ok = await swalConfirm({
      title: 'Reject Quotation?',
      text: 'The customer will be recorded as rejecting the quotation.',
      confirmText: 'Reject',
    });
    if (ok) custRejectMutation.mutate();
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (isLoading) return <SkeletonPage />;

  if (!inspection) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-20 text-center">
        <FontAwesomeIcon icon={faExclamationTriangle} className="h-10 w-10 text-muted-foreground" />
        <p className="text-lg font-semibold text-foreground">Inspection not found</p>
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2 h-4 w-4" />
          Go back
        </Button>
      </div>
    );
  }

  const hasCommercialAccess = can('inspection:view-commercial');

  const isOwner = inspection.assignedEngineerId === currentUserId;
  const isActiveInspection = !['CLOSED', 'CANCELLED', 'COMPLETED', 'REVIEWED'].includes(
    inspection.status,
  );

  const canStart = inspection.status === 'ASSIGNED' && can('inspection:start');
  const canSubmit = inspection.status === 'IN_PROGRESS' && can('inspection:submit');
  const canOpsReview = inspection.status === 'COMPLETED' && can('inspection:operations-review');
  // ── B6.19 — lifecycle actions ──────────────────────────────────────────────
  // Permission AND backend-declared eligibility. Both are required: the
  // permission decides who may ever close, the eligibility query decides whether
  // this particular job can be closed right now (DEC-2 gives two routes to that,
  // and the UI deliberately does not try to work out which one applies).
  const isTerminal = inspection.status === 'CLOSED' || inspection.status === 'CANCELLED';
  const canCloseJob = can('inspection:close') && (closure?.eligible ?? false);
  const canCancelJob = can('inspection:cancel') && !isTerminal;
  const canAcknowledgeReturn =
    can('inspection:operations-review') &&
    !isTerminal &&
    inspection.commercialReviewStatus === 'RETURNED_TO_OPERATIONS';
  const isOwnerEditable =
    (inspection.status === 'IN_PROGRESS' || inspection.status === 'TRANSFER_REQUESTED') && isOwner;
  const canModifyFindings =
    inspection.status === 'IN_PROGRESS' && isOwner && can('inspection:findings:create');
  const canUploadAttachments = isOwnerEditable && can('inspection:attachments:upload');
  const canDeleteMedia = isOwnerEditable && can('inspection:attachments:delete');
  const canCreateNote =
    !['CLOSED', 'CANCELLED', 'COMPLETED', 'REVIEWED'].includes(inspection.status) &&
    can('inspection:notes:create');
  const canCreateFindingNote =
    !['CLOSED', 'CANCELLED', 'COMPLETED', 'REVIEWED'].includes(inspection.status) &&
    can('inspection:finding-notes:create');
  const canCreateAuthorization =
    !['CLOSED', 'CANCELLED', 'COMPLETED', 'REVIEWED'].includes(inspection.status) &&
    can('inspection:authorization:create');
  const canRespondAuthorization = can('inspection:authorization:respond');
  const canCommercialSend =
    inspection.operationsReviewStatus === 'APPROVED_FOR_COMMERCIAL' &&
    inspection.commercialReviewStatus === 'PENDING' &&
    hasCommercialAccess;
  const canCommercialReturn =
    inspection.commercialReviewStatus === 'SENT_TO_CUSTOMER' && hasCommercialAccess;
  // Staff-recorded customer decisions. Reject / Request Modification follow the
  // endpoints' own gate (quotation with the customer); Approve additionally
  // needs an unanswered round because it now carries the Work Order.
  const custDecisions = staffCustomerDecisionActions(
    inspection,
    can('inspection:customer-approval'),
  );
  const canCustApproval = custDecisions.reject || custDecisions.requestModification;
  // D-129 — quotation PDF gate (mirrors the server rule) and Work Order acceptance
  const sendGate = quotationSendGate(inspection);
  const canAcceptWorkOrder =
    hasCommercialAccess &&
    can('inspection:commercial:send') &&
    isWorkOrderAwaitingAcceptance(inspection);

  const canTransfer =
    isOwner &&
    inspection.status !== 'TRANSFER_REQUESTED' &&
    isActiveInspection &&
    can('inspection:ownership:transfer:initiate');
  const canClaim = !inspection.assignedEngineerId && isActiveInspection && can('inspection:claim');
  const canRequestTakeover =
    !!inspection.assignedEngineerId &&
    !isOwner &&
    isActiveInspection &&
    inspection.status !== 'TRANSFER_REQUESTED' &&
    can('inspection:ownership:takeover:request');
  const canForceTransfer = isActiveInspection && can('inspection:ownership:force-transfer');
  const canViewHistory = can('inspection:ownership:history');

  const findings = findingsData?.data ?? [];

  // Section grouping (final polish): only render a group's label when at least
  // one of its sections is visible for this user/state.
  const showOwnershipGroup =
    isOwner || inspection.pendingTransferToId === currentUserId || canViewHistory;
  const showProcedureGroup = can('procedure:view-execution');

  const anyActionPending =
    startMutation.isPending ||
    submitMutation.isPending ||
    opsApproveMutation.isPending ||
    custApproveMutation.isPending ||
    custRejectMutation.isPending ||
    attachQuotationDocMutation.isPending ||
    removeQuotationDocMutation.isPending ||
    acceptWorkOrderMutation.isPending ||
    revokeTransferMutation.isPending ||
    claimMutation.isPending;

  return (
    <div className="space-y-5">
      {/* Sticky inspection header — pins to top of scroll container while navigating the page.
          Opaque below `lg`: the translucent-with-blur treatment reads as a tasteful
          60px strip on desktop, but the mobile header is twice that and content
          sliding under a band that size looks like a rendering fault rather than
          an effect. Desktop keeps `bg-background/95` + blur exactly as before. */}
      <div className="sticky top-0 z-10 -mx-6 px-6 py-2 bg-background border-b border-border lg:py-3 lg:bg-background/95 lg:backdrop-blur-sm">
        {/* Below `xl` this is two deliberate tiers — identity, then actions —
            rather than one row left to flex-wrap. The action groups cannot give
            up width, so in a single row they crush the identity block until its
            badges fragment one per line and the buttons overlap them.

            The threshold is `xl`, not `md`, because the sidebar is what decides
            whether a single row actually fits: at 1024 it is 264px wide AND the
            action buttons are ~460px, leaving the identity ~184px — enough to
            wrap the badges one per line. From `xl` up there is real room and
            this is the original single row, unchanged.

            Touch targets below use `lg` instead. Different concern, different
            threshold: a 1024-wide laptop wants the roomier two-tier layout but
            not 44px finger-sized buttons. */}
        <div className="flex flex-col gap-2 xl:flex-row xl:items-center xl:gap-3">
          <div className="flex min-w-0 items-center gap-3 xl:flex-1">
            <button
              onClick={() => navigate(-1)}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors lg:h-8 lg:w-8"
              aria-label="Back"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="h-4 w-4" />
            </button>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                {/* The inspection number is an identifier — never break it across
                    lines, however narrow the viewport gets. */}
                <span className="whitespace-nowrap text-sm font-bold text-foreground font-mono">
                  {inspection.inspectionNumber}
                </span>

                {/* Status, elapsed time and finding count describe the same thing —
                    where this job stands — so they travel as one unit. Grouping
                    them means a narrow viewport wraps the whole group onto its own
                    line instead of fragmenting it one badge at a time. */}
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span
                    className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_BADGE[inspection.status]}`}
                  >
                    {INSPECTION_STATUS_LABELS[inspection.status]}
                  </span>
                  {/* How long the current engineer has held this job, from the
                    persisted `assignedAt`. Renders nothing when nobody holds it. */}
                  <InspectionElapsedIndicator assignedAt={inspection.assignedAt} />
                  {/* Hidden below `lg`, where its ~63px is the difference between
                      the identity fitting on two lines and needing three. The
                      count is not lost: the Inspection Details card carries a
                      "Total Findings" row and the Findings section title reads
                      "Findings (n)", both of which are on screen without
                      scrolling on a phone. */}
                  {inspection.totalFindingsCount > 0 && (
                    <span className="hidden whitespace-nowrap text-xs text-muted-foreground lg:inline">
                      {inspection.totalFindingsCount}{' '}
                      {inspection.totalFindingsCount === 1 ? 'finding' : 'findings'}
                    </span>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground truncate">
                {inspection.equipmentName}
                {inspection.equipmentAssetNumber && ` · ${inspection.equipmentAssetNumber}`}
                {(inspection.customerOrganizationName ?? inspection.customerName) &&
                  ` · ${inspection.customerOrganizationName ?? inspection.customerName}`}
              </p>
            </div>
          </div>

          {/* The action area. Buttons wrap as whole buttons — `shrink-0` stops
              flexbox compressing one until its label wraps inside a fixed-height
              box — and each clears the 44px touch target on mobile, dropping
              back to the desktop size from `lg`. */}
          <div className="flex flex-wrap items-center gap-2 xl:flex-nowrap xl:gap-3 xl:shrink-0 [&_button]:min-h-[44px] [&_button]:shrink-0 [&_button]:whitespace-nowrap lg:[&_button]:min-h-0">
            {/* Ownership action buttons */}
            {(canClaim || canTransfer || canRequestTakeover || canForceTransfer) && (
              <div className="flex flex-wrap items-center gap-1.5 xl:shrink-0">
                {canClaim && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => claimMutation.mutate()}
                    disabled={anyActionPending}
                  >
                    {claimMutation.isPending ? (
                      <FontAwesomeIcon icon={faSpinner} className="mr-1.5 h-3 w-3 animate-spin" />
                    ) : (
                      <FontAwesomeIcon icon={faPlus} className="mr-1.5 h-3 w-3" />
                    )}
                    Claim Inspection
                  </Button>
                )}
                {canTransfer && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setTransferOpen(true)}
                    disabled={anyActionPending}
                  >
                    <FontAwesomeIcon icon={faArrowRightArrowLeft} className="mr-1.5 h-3 w-3" />
                    Transfer
                  </Button>
                )}
                {canRequestTakeover && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="hidden lg:inline-flex"
                    onClick={() => setTakeoverOpen(true)}
                    disabled={anyActionPending}
                  >
                    <FontAwesomeIcon icon={faUserPlus} className="mr-1.5 h-3 w-3" />
                    Request Takeover
                  </Button>
                )}
                {canForceTransfer && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="hidden border-destructive/40 text-destructive hover:bg-destructive/5 lg:inline-flex"
                    onClick={() => setForceTransferOpen(true)}
                    disabled={anyActionPending}
                  >
                    <FontAwesomeIcon icon={faBolt} className="mr-1.5 h-3 w-3" />
                    Force Transfer
                  </Button>
                )}
              </div>
            )}

            {/* Primary action buttons — compact */}
            <div className="flex flex-wrap items-center gap-2 xl:shrink-0">
              {canStart && (
                <Button
                  size="sm"
                  onClick={() => startMutation.mutate()}
                  disabled={anyActionPending}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {startMutation.isPending ? (
                    <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                  ) : (
                    <FontAwesomeIcon icon={faPlay} className="mr-2 h-3 w-3" />
                  )}
                  Start
                </Button>
              )}

              {canSubmit && (
                <Button size="sm" onClick={() => void handleSubmit()} disabled={anyActionPending}>
                  {submitMutation.isPending && (
                    <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                  )}
                  Submit
                </Button>
              )}

              {canOpsReview && (
                <>
                  <Button
                    size="sm"
                    className="is-primary"
                    onClick={() => void handleOpsApprove()}
                    disabled={anyActionPending}
                  >
                    {opsApproveMutation.isPending && (
                      <FontAwesomeIcon icon={faSpinner} className="mr-1.5 animate-spin h-3 w-3" />
                    )}
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setOpsReturnOpen(true)}
                    disabled={anyActionPending}
                  >
                    Return
                  </Button>
                </>
              )}

              {/* ── B6.19 — job lifecycle ───────────────────────────────────── */}
              {canAcknowledgeReturn && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => ackReturnMutation.mutate()}
                  disabled={anyActionPending || ackReturnMutation.isPending}
                  title="Commercial returned this job. Put it back in the Operations review queue."
                >
                  {ackReturnMutation.isPending && (
                    <FontAwesomeIcon icon={faSpinner} className="mr-1.5 h-3 w-3 animate-spin" />
                  )}
                  Acknowledge Return
                </Button>
              )}

              {canCloseJob && (
                <Button
                  size="sm"
                  className="is-primary"
                  onClick={() => void handleClose()}
                  disabled={anyActionPending || closeMutation.isPending}
                >
                  {closeMutation.isPending && (
                    <FontAwesomeIcon icon={faSpinner} className="mr-1.5 h-3 w-3 animate-spin" />
                  )}
                  Close Job
                </Button>
              )}

              {canCancelJob && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setCancelOpen(true)}
                  disabled={anyActionPending}
                  className="hidden text-destructive hover:bg-destructive/10 lg:inline-flex"
                >
                  Cancel Job
                </Button>
              )}
            </div>

            {/* Below `lg` the three secondary actions above are hidden and live
                here instead, so the header keeps a single 44px action row with
                Submit as the one prominent control. Same permission flags, same
                handlers — this is a change of placement, not of behaviour.

                `ml-auto` pins the trigger to the right edge. That is not
                cosmetic: the menu is anchored `right-0` to this wrapper, so with
                the trigger sitting mid-row next to Submit the panel opened 52px
                past the left edge of the screen. */}
            <HeaderActionsMenu
              className="ml-auto lg:hidden"
              label={`More actions for ${inspection.inspectionNumber}`}
              items={[
                ...(canRequestTakeover
                  ? [
                      {
                        label: 'Request Takeover',
                        icon: faUserPlus,
                        onSelect: () => setTakeoverOpen(true),
                      },
                    ]
                  : []),
                ...(canForceTransfer
                  ? [
                      {
                        label: 'Force Transfer',
                        icon: faBolt,
                        onSelect: () => setForceTransferOpen(true),
                      },
                    ]
                  : []),
                ...(canCancelJob
                  ? [
                      {
                        label: 'Cancel Job',
                        icon: faXmark,
                        onSelect: () => setCancelOpen(true),
                        danger: true,
                      },
                    ]
                  : []),
              ]}
            />
          </div>
        </div>
      </div>

      {/* ── Transfer Pending Banner (owner view) ─────────────────────────── */}
      {isOwner && inspection.status === 'TRANSFER_REQUESTED' && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 px-5 py-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-1">
              <p className="text-sm font-semibold text-amber-800">Transfer Pending</p>
              <p className="text-sm text-amber-700">
                Waiting for{' '}
                <span className="font-medium">
                  {inspection.pendingTransferToName ?? 'another engineer'}
                </span>{' '}
                to respond · <ElapsedTimer since={inspection.updatedAt} />
              </p>
              {inspection.pendingTransferReason && (
                <p className="text-xs text-amber-600">Reason: {inspection.pendingTransferReason}</p>
              )}
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="shrink-0 border-amber-500/40 text-amber-800 hover:bg-amber-500/10"
              onClick={() => revokeTransferMutation.mutate()}
              disabled={revokeTransferMutation.isPending}
            >
              {revokeTransferMutation.isPending ? (
                <FontAwesomeIcon icon={faSpinner} className="mr-1.5 h-3 w-3 animate-spin" />
              ) : (
                <FontAwesomeIcon icon={faXmark} className="mr-1.5 h-3 w-3" />
              )}
              Revoke Transfer
            </Button>
          </div>
        </div>
      )}

      {/* ══ Group: Inspection ═════════════════════════════════════════════════ */}
      <GroupLabel icon={faClipboardCheck} title="Inspection" />

      {/* ── Core Details ──────────────────────────────────────────────────── */}
      <SectionCard icon={faClipboardList} title="Inspection Details">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          <DetailRow
            label="Equipment"
            value={
              <span>
                {inspection.equipmentName}
                {inspection.equipmentAssetNumber && (
                  <span className="ml-1.5 font-mono text-xs text-muted-foreground">
                    {inspection.equipmentAssetNumber}
                  </span>
                )}
              </span>
            }
          />
          {inspection.equipmentSerialNumber && (
            <DetailRow
              label="Serial Number"
              value={<span className="font-mono">{inspection.equipmentSerialNumber}</span>}
            />
          )}
          {/* Customer = the ORGANIZATION, falling back to the contact only when
              no organization is recorded — the convention already used by the
              equipment and intake modules. The contact keeps its own row, but
              only when an organization exists; otherwise the same value would
              be printed twice. */}
          <DetailRow
            label="Customer"
            value={inspection.customerOrganizationName ?? inspection.customerName}
          />
          {inspection.customerOrganizationName && inspection.customerName && (
            <DetailRow label="Customer Contact" value={inspection.customerName} />
          )}
          {inspection.customerLocationName && (
            <DetailRow label="Location" value={inspection.customerLocationName} />
          )}
          <DetailRow label="Service Type" value={inspection.serviceTypeName} />
          <DetailRow label="Priority" value={inspection.priorityName} />
          <DetailRow label="Warranty" value={WARRANTY_STATUS_LABELS[inspection.warrantyStatus]} />
          {inspection.warrantyTypeName && (
            <DetailRow label="Warranty Type" value={inspection.warrantyTypeName} />
          )}
          <DetailRow label="Assigned Engineer" value={inspection.assignedEngineerName} />
          <DetailRow label="Assigned By" value={inspection.assignedByName} />
          <DetailRow label="Scheduled" value={fmtDate(inspection.scheduledAt)} />
          <DetailRow label="Started" value={fmtDateTime(inspection.startedAt)} />
          <DetailRow label="Completed" value={fmtDateTime(inspection.completedAt)} />
          <DetailRow label="Total Findings" value={inspection.totalFindingsCount} />
          {inspection.batchReferenceNo && (
            <DetailRow label="Batch Reference" value={inspection.batchReferenceNo} />
          )}
          {inspection.intakeNumber && (
            <DetailRow
              label="Inward #"
              value={<span className="font-mono">{inspection.intakeNumber}</span>}
            />
          )}
          <DetailRow label="Created By" value={inspection.createdByName} />
        </div>

        {inspection.customerRequestedScope && (
          <div className="mt-4">
            <span className="text-xs text-muted-foreground">Customer Requested Scope</span>
            <p className="mt-1 text-sm text-foreground">{inspection.customerRequestedScope}</p>
          </div>
        )}

        {inspection.notes && (
          <div className="mt-4">
            <span className="text-xs text-muted-foreground">Notes</span>
            <p className="mt-1 text-sm text-foreground">{inspection.notes}</p>
          </div>
        )}
      </SectionCard>

      {/* ── As-Received / As-Found Condition (Group 4) ─────────────────────
          Renders nothing when the inspection has no checklist — i.e. every
          inspection created before Group 4. The section never creates one. */}
      <SectionCard icon={faListCheck} title="As-Received / As-Found Condition">
        <ConditionChecksSection
          inspectionId={id!}
          canEdit={isOwnerEditable && can('inspection:condition-checks:manage')}
        />
      </SectionCard>

      {/* ── Findings ──────────────────────────────────────────────────────── */}
      <SectionCard
        icon={faMagnifyingGlass}
        title={`Findings (${findings.length})`}
        actions={
          canModifyFindings ? (
            <Button
              size="sm"
              className="is-primary"
              onClick={() => {
                setEditFinding(null);
                setFindingForm({
                  severity: 'MEDIUM',
                  title: '',
                  description: '',
                  componentArea: '',
                  insideRequestedScope: false,
                  warrantyApplicable: false,
                  recommendedAction: '',
                  recommendedPartsSummary: '',
                  estimatedRepairHours: '',
                });
                setFindingModalOpen(true);
              }}
            >
              <FontAwesomeIcon icon={faPlus} className="mr-1.5 h-3 w-3" />
              Add Finding
            </Button>
          ) : undefined
        }
      >
        {findingsLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
            Loading findings…
          </div>
        ) : findings.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Add a finding to record photos, videos, documents, notes, and authorization requests.
          </p>
        ) : (
          <div className="space-y-2">
            {findings.map((finding) => (
              <FindingAccordionItem
                key={finding.id}
                finding={finding}
                inspectionId={id!}
                canModify={canModifyFindings}
                canUploadAttachments={canUploadAttachments}
                canDeleteMedia={canDeleteMedia}
                canCreateFindingNote={canCreateFindingNote}
                canCreateAuthorization={canCreateAuthorization}
                canRespondAuthorization={canRespondAuthorization}
                isNewlyCreated={finding.id === latestCreatedFindingId}
                onEdit={(f) => {
                  setEditFinding(f);
                  setFindingForm({
                    severity: f.severity,
                    title: f.title,
                    description: f.description,
                    componentArea: f.componentArea ?? '',
                    insideRequestedScope: f.insideRequestedScope,
                    warrantyApplicable: f.warrantyApplicable,
                    recommendedAction: f.recommendedAction ?? '',
                    recommendedPartsSummary: f.recommendedPartsSummary ?? '',
                    estimatedRepairHours: f.estimatedRepairHours ?? '',
                  });
                  setFindingModalOpen(true);
                }}
                onDelete={(fId) => {
                  void swalConfirm({
                    title: 'Delete Finding?',
                    text: 'This cannot be undone.',
                    confirmText: 'Delete',
                  }).then((ok) => {
                    if (ok) deleteFindingMutation.mutate(fId);
                  });
                }}
              />
            ))}
          </div>
        )}
      </SectionCard>

      {/* ── Operations Review ─────────────────────────────────────────────── */}
      <SectionCard
        icon={faUserCheck}
        title="Operations Review"
        actions={
          <>
            {canOpsReview && (
              <>
                <Button
                  size="sm"
                  className="is-primary"
                  onClick={() => void handleOpsApprove()}
                  disabled={anyActionPending}
                >
                  {opsApproveMutation.isPending && (
                    <FontAwesomeIcon icon={faSpinner} className="mr-1.5 animate-spin h-3 w-3" />
                  )}
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setOpsReturnOpen(true)}
                  disabled={anyActionPending}
                >
                  Return
                </Button>
              </>
            )}
          </>
        }
      >
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <DetailRow
            label="Status"
            value={
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${OPS_BADGE[inspection.operationsReviewStatus]}`}
              >
                {OPS_REVIEW_STATUS_LABELS[inspection.operationsReviewStatus]}
              </span>
            }
          />
          <DetailRow label="Reviewed By" value={inspection.operationsReviewedByName} />
          <DetailRow label="Reviewed At" value={fmtDateTime(inspection.operationsReviewedAt)} />
        </div>

        {inspection.operationsRecommendation && (
          <div className="mt-4">
            <span className="text-xs text-muted-foreground">Recommendation</span>
            <p className="mt-1 text-sm text-foreground">{inspection.operationsRecommendation}</p>
          </div>
        )}

        {inspection.operationsReturnReason && (
          <div className="mt-4 rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3">
            <span className="text-xs font-medium text-destructive">Return Reason</span>
            <p className="mt-1 text-sm text-foreground">{inspection.operationsReturnReason}</p>
          </div>
        )}
      </SectionCard>

      {/* ── Commercial Review (gated) ─────────────────────────────────────── */}
      {hasCommercialAccess && (
        <SectionCard
          icon={faFileInvoiceDollar}
          title="Commercial Review"
          actions={
            <>
              {canCommercialSend && (
                <Button size="sm" className="is-primary" onClick={() => setCommSendOpen(true)}>
                  Send to Customer
                </Button>
              )}
              {canCommercialReturn && (
                <Button size="sm" variant="secondary" onClick={() => setCommReturnOpen(true)}>
                  Return to Operations
                </Button>
              )}
            </>
          }
        >
          {inspection.commercialReviewStatus ? (
            <>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                <DetailRow
                  label="Status"
                  value={
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${COMMERCIAL_BADGE[inspection.commercialReviewStatus]}`}
                    >
                      {COMMERCIAL_STATUS_LABELS[inspection.commercialReviewStatus]}
                    </span>
                  }
                />
                <DetailRow label="Reviewed By" value={inspection.commercialReviewedByName} />
                <DetailRow label="Sent At" value={fmtDateTime(inspection.commercialSentAt)} />
                {inspection.quotationReference && (
                  <DetailRow label="Quotation Ref" value={inspection.quotationReference} />
                )}
                {/* D-129 — the quotation PDF that was (or will be) sent */}
                <DetailRow
                  label="Quotation Document"
                  value={
                    inspection.quotationDocumentName ? (
                      <span className="inline-flex items-center gap-2">
                        <FontAwesomeIcon
                          icon={faFilePdf}
                          className="h-3.5 w-3.5 text-destructive"
                        />
                        <span className="truncate">{inspection.quotationDocumentName}</span>
                        {commercialLinks?.quotationDocumentUrl && (
                          <a
                            href={commercialLinks.quotationDocumentUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary underline-offset-2 hover:underline"
                            title="Download quotation PDF"
                          >
                            <FontAwesomeIcon icon={faDownload} className="h-3.5 w-3.5" />
                          </a>
                        )}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">Not attached</span>
                    )
                  }
                />
                {inspection.quotationSentDocumentId &&
                  inspection.quotationDocumentId !== inspection.quotationSentDocumentId && (
                    <DetailRow
                      label="Sent to Customer"
                      value={
                        <span className="text-amber-700" data-testid="quotation-unsent-replacement">
                          {inspection.quotationSentDocumentName ?? 'previous document'} — the
                          replacement above is not visible to the customer until you send again
                        </span>
                      }
                    />
                  )}
              </div>

              {inspection.commercialSummary && (
                <div className="mt-4">
                  <span className="text-xs text-muted-foreground">Commercial Summary</span>
                  <p className="mt-1 text-sm text-foreground">{inspection.commercialSummary}</p>
                </div>
              )}

              {inspection.commercialReturnReason && (
                <div className="mt-4 rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3">
                  <span className="text-xs font-medium text-destructive">Return Reason</span>
                  <p className="mt-1 text-sm text-foreground">
                    {inspection.commercialReturnReason}
                  </p>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              Commercial review is not yet available for this inspection.
            </p>
          )}
        </SectionCard>
      )}

      {/* ── Customer Approval (gated) ─────────────────────────────────────── */}
      {hasCommercialAccess && (
        <SectionCard
          icon={faFileSignature}
          title="Customer Approval"
          actions={
            <>
              {canAcceptWorkOrder && (
                <Button
                  size="sm"
                  className="is-primary"
                  onClick={() => void handleAcceptWorkOrder()}
                  disabled={anyActionPending}
                >
                  {acceptWorkOrderMutation.isPending && (
                    <FontAwesomeIcon icon={faSpinner} className="mr-1.5 animate-spin h-3 w-3" />
                  )}
                  Accept Work Order
                </Button>
              )}
              {canCustApproval && (
                <>
                  {custDecisions.approve && (
                    <Button
                      size="sm"
                      className="is-primary"
                      onClick={handleCustApprove}
                      disabled={anyActionPending}
                    >
                      Approve
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => void handleCustReject()}
                    disabled={anyActionPending}
                  >
                    {custRejectMutation.isPending && (
                      <FontAwesomeIcon icon={faSpinner} className="mr-1.5 animate-spin h-3 w-3" />
                    )}
                    Reject
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setCustModOpen(true)}
                    disabled={anyActionPending}
                  >
                    Request Modification
                  </Button>
                </>
              )}
            </>
          }
        >
          {inspection.customerApprovalStatus ? (
            <>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                <DetailRow
                  label="Status"
                  value={
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${CUSTOMER_BADGE[inspection.customerApprovalStatus]}`}
                    >
                      {CUSTOMER_APPROVAL_LABELS[inspection.customerApprovalStatus]}
                    </span>
                  }
                />
                <DetailRow
                  label="Responded At"
                  value={fmtDateTime(inspection.customerApprovalRespondedAt)}
                />
                {inspection.customerApprovalContactName && (
                  <DetailRow label="Approved By" value={inspection.customerApprovalContactName} />
                )}
              </div>

              {inspection.customerModificationReason && (
                <div className="mt-4 rounded-lg border border-amber-500/20 bg-amber-500/5 px-4 py-3">
                  <span className="text-xs font-medium text-amber-700">Modification Reason</span>
                  <p className="mt-1 text-sm text-foreground">
                    {inspection.customerModificationReason}
                  </p>
                </div>
              )}
              {/* D-129 — the customer's Work Order and Commercial's acceptance of it */}
              {inspection.workOrderNumber && (
                <div
                  className="mt-4 rounded-lg border border-border bg-muted/30 px-4 py-3"
                  data-testid="staff-work-order"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-xs font-medium text-muted-foreground">
                      Customer Work Order
                    </span>
                    {inspection.workOrderReviewStatus && (
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          inspection.workOrderReviewStatus === 'ACCEPTED'
                            ? 'bg-emerald-500/10 text-emerald-700'
                            : 'bg-amber-500/10 text-amber-700'
                        }`}
                      >
                        {WORK_ORDER_REVIEW_LABELS[inspection.workOrderReviewStatus]}
                      </span>
                    )}
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-4 sm:grid-cols-3">
                    <DetailRow label="Work Order Number" value={inspection.workOrderNumber} />
                    <DetailRow
                      label="Work Order PDF"
                      value={
                        inspection.workOrderDocumentName ? (
                          <span className="inline-flex items-center gap-2">
                            <FontAwesomeIcon
                              icon={faFilePdf}
                              className="h-3.5 w-3.5 text-destructive"
                            />
                            <span className="truncate">{inspection.workOrderDocumentName}</span>
                            {commercialLinks?.workOrderDocumentUrl && (
                              <a
                                href={commercialLinks.workOrderDocumentUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="text-primary underline-offset-2 hover:underline"
                                title="Download Work Order PDF"
                              >
                                <FontAwesomeIcon icon={faDownload} className="h-3.5 w-3.5" />
                              </a>
                            )}
                          </span>
                        ) : null
                      }
                    />
                    {inspection.workOrderAcceptedAt && (
                      <DetailRow
                        label="Accepted"
                        value={`${fmtDateTime(inspection.workOrderAcceptedAt)}${
                          inspection.workOrderAcceptedByName
                            ? ` · ${inspection.workOrderAcceptedByName}`
                            : ''
                        }`}
                      />
                    )}
                  </div>
                  {inspection.workOrderDescription && (
                    <div className="mt-3">
                      <span className="text-xs text-muted-foreground">Work Order Description</span>
                      <p className="mt-1 whitespace-pre-line text-sm text-foreground">
                        {inspection.workOrderDescription}
                      </p>
                    </div>
                  )}
                  {inspection.workOrderReviewStatus === 'PENDING_COMMERCIAL_REVIEW' && (
                    <p className="mt-3 text-xs text-muted-foreground">
                      Operations cannot continue the job until Commercial accepts this Work Order.
                    </p>
                  )}
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              Awaiting customer approval after commercial review is sent.
            </p>
          )}
        </SectionCard>
      )}

      {/* ── Modification Requests (gated, D-131 M6) ───────────────────────── */}
      {hasCommercialAccess && (
        <SectionCard icon={faComments} title="Modification Requests">
          <ModificationPanel inspectionId={id!} />
        </SectionCard>
      )}

      {/* ══ Group: Report ═════════════════════════════════════════════════════ */}
      <GroupLabel icon={faFileLines} title="Report" />

      {/* ── Inspection Report ─────────────────────────────────────────────── */}
      <InspectionReportSection inspectionId={id!} warrantyStatus={inspection.warrantyStatus} />

      {/* ── Internal Notes ────────────────────────────────────────────────── */}
      <InternalNotesSection inspectionId={id!} canCreate={canCreateNote} />

      {/* ══ Group: Ownership ══════════════════════════════════════════════════ */}
      {showOwnershipGroup && <GroupLabel icon={faRightLeft} title="Ownership" />}

      {/* ── Ownership Requests ──────────────────────────────────────────────── */}
      {inspection && (isOwner || inspection.pendingTransferToId === currentUserId) && (
        <SectionCard icon={faRightLeft} title="Ownership Requests">
          <PendingOwnershipRequests inspection={inspection} currentUserId={currentUserId} />
        </SectionCard>
      )}

      {/* ── Ownership History ───────────────────────────────────────────────── */}
      {canViewHistory && (
        <SectionCard
          icon={faClockRotateLeft}
          title="Ownership History"
          actions={
            <button
              onClick={() => setHistoryOpen((v) => !v)}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              {historyOpen ? (
                <>
                  Collapse <FontAwesomeIcon icon={faChevronUp} className="h-3 w-3" />
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faHistory} className="h-3 w-3" />
                  Expand
                  <FontAwesomeIcon icon={faChevronDown} className="h-3 w-3" />
                </>
              )}
            </button>
          }
        >
          {historyOpen ? (
            <OwnershipHistoryPanel inspectionId={id!} />
          ) : (
            <p className="text-sm text-muted-foreground italic">
              Click Expand to view the full ownership change timeline.
            </p>
          )}
        </SectionCard>
      )}

      {/* ── Procedure Execution ─────────────────────────���─────────────────── */}
      {showProcedureGroup && <GroupLabel icon={faListCheck} title="Procedure" />}
      {showProcedureGroup && (
        <ProcedureExecutionPanel inspectionId={id!} canExecute={can('procedure:execute')} />
      )}

      {/* ── Modals ─────────────────────────────────────────────────────────── */}

      {/* Finding CRUD */}
      <Modal
        open={findingModalOpen}
        onClose={() => {
          setFindingModalOpen(false);
          setEditFinding(null);
        }}
        title={editFinding ? 'Edit Finding' : 'Add Finding'}
        size="lg"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label="Severity" htmlFor="f-sev" required>
              <select
                id="f-sev"
                value={findingForm.severity}
                onChange={(e) =>
                  setFindingForm((p) => ({
                    ...p,
                    severity: e.target.value as InspectionFindingSeverity,
                  }))
                }
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground"
              >
                <option value="CRITICAL">Critical</option>
                <option value="HIGH">High</option>
                <option value="MEDIUM">Medium</option>
                <option value="LOW">Low</option>
              </select>
            </FormField>
            <FormField label="Component / Area" htmlFor="f-comp">
              <Input
                id="f-comp"
                value={findingForm.componentArea}
                onChange={(e) => setFindingForm((p) => ({ ...p, componentArea: e.target.value }))}
                placeholder="e.g. Engine, Hydraulics…"
                maxLength={200}
              />
            </FormField>
          </div>
          <FormField label="Title" htmlFor="f-title" required>
            <Input
              id="f-title"
              value={findingForm.title}
              onChange={(e) => setFindingForm((p) => ({ ...p, title: e.target.value }))}
              placeholder="Brief title of the finding"
              maxLength={500}
            />
          </FormField>
          <FormField label="Description" htmlFor="f-desc" required>
            <Textarea
              id="f-desc"
              value={findingForm.description}
              onChange={(e) => setFindingForm((p) => ({ ...p, description: e.target.value }))}
              placeholder="Detailed description of what was found…"
              rows={4}
              maxLength={5000}
            />
          </FormField>
          <FormField label="Recommended Action" htmlFor="f-action">
            <Textarea
              id="f-action"
              value={findingForm.recommendedAction}
              onChange={(e) => setFindingForm((p) => ({ ...p, recommendedAction: e.target.value }))}
              placeholder="What action should be taken?"
              rows={2}
              maxLength={2000}
            />
          </FormField>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label="Recommended Parts" htmlFor="f-parts">
              <Input
                id="f-parts"
                value={findingForm.recommendedPartsSummary}
                onChange={(e) =>
                  setFindingForm((p) => ({ ...p, recommendedPartsSummary: e.target.value }))
                }
                placeholder="Part names/codes"
                maxLength={1000}
              />
            </FormField>
            <FormField label="Est. Repair Hours" htmlFor="f-hours">
              <Input
                id="f-hours"
                value={findingForm.estimatedRepairHours}
                onChange={(e) =>
                  setFindingForm((p) => ({ ...p, estimatedRepairHours: e.target.value }))
                }
                placeholder="e.g. 2.5"
                maxLength={20}
              />
            </FormField>
          </div>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={findingForm.insideRequestedScope}
                onChange={(e) =>
                  setFindingForm((p) => ({ ...p, insideRequestedScope: e.target.checked }))
                }
                className="rounded"
              />
              Inside requested scope
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={findingForm.warrantyApplicable}
                onChange={(e) =>
                  setFindingForm((p) => ({ ...p, warrantyApplicable: e.target.checked }))
                }
                className="rounded"
              />
              Warranty applicable
            </label>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="ghost"
              onClick={() => {
                setFindingModalOpen(false);
                setEditFinding(null);
              }}
              disabled={createFindingMutation.isPending || updateFindingMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!findingForm.title.trim() || !findingForm.description.trim()) {
                  toast.error('Title and description are required');
                  return;
                }
                const { estimatedRepairHours: rawHours, ...baseFields } = findingForm;
                const submitPayload = rawHours
                  ? { ...baseFields, estimatedRepairHours: rawHours }
                  : baseFields;
                if (editFinding) {
                  updateFindingMutation.mutate({ fId: editFinding.id, payload: submitPayload });
                } else {
                  createFindingMutation.mutate(submitPayload);
                }
              }}
              disabled={createFindingMutation.isPending || updateFindingMutation.isPending}
            >
              {(createFindingMutation.isPending || updateFindingMutation.isPending) && (
                <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
              )}
              {editFinding ? 'Update' : 'Add Finding'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Ops Return */}
      <Modal
        open={opsReturnOpen}
        onClose={() => {
          setOpsReturnOpen(false);
          setOpsReturnReason('');
        }}
        title="Return to Engineer"
        size="md"
      >
        <div className="space-y-4">
          <FormField label="Return Reason" htmlFor="ops-return-reason" required>
            <Textarea
              id="ops-return-reason"
              value={opsReturnReason}
              onChange={(e) => setOpsReturnReason(e.target.value)}
              placeholder="Describe what needs to be addressed…"
              rows={4}
              maxLength={2000}
              disabled={opsReturnMutation.isPending}
            />
          </FormField>
          <div className="flex justify-end gap-3">
            <Button
              variant="ghost"
              onClick={() => {
                setOpsReturnOpen(false);
                setOpsReturnReason('');
              }}
              disabled={opsReturnMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => opsReturnMutation.mutate(opsReturnReason)}
              disabled={!opsReturnReason.trim() || opsReturnMutation.isPending}
            >
              {opsReturnMutation.isPending && (
                <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
              )}
              Return
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── B6.19 — Cancel Job. Reason is MANDATORY: cancellation abandons a job
          created for a real customer and a real machine, it is irreversible
          through the product, and the recorded reason is the only thing that
          makes it auditable afterwards. Deliberately the same shape as the
          Return-to-Engineer dialog above. ─────────────────────────────────── */}
      <Modal
        open={cancelOpen}
        onClose={() => {
          setCancelOpen(false);
          setCancelReason('');
        }}
        title="Cancel Job"
        size="md"
      >
        <div className="space-y-4">
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
            Cancelling is permanent. The job will not appear in any active queue again and cannot be
            reopened.
          </div>
          <FormField label="Cancellation Reason" htmlFor="cancel-reason" required>
            <Textarea
              id="cancel-reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Why is this job being cancelled?"
              rows={4}
              maxLength={2000}
              disabled={cancelMutation.isPending}
            />
          </FormField>
          <div className="flex justify-end gap-3">
            <Button
              variant="ghost"
              onClick={() => {
                setCancelOpen(false);
                setCancelReason('');
              }}
              disabled={cancelMutation.isPending}
            >
              Keep Job
            </Button>
            <Button
              onClick={() => cancelMutation.mutate(cancelReason)}
              disabled={!cancelReason.trim() || cancelMutation.isPending}
              className="is-primary"
            >
              {cancelMutation.isPending && (
                <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
              )}
              Cancel Job
            </Button>
          </div>
        </div>
      </Modal>

      {/* Commercial Send */}
      <Modal
        open={commSendOpen}
        onClose={() => {
          setCommSendOpen(false);
          setCommSendSummary('');
          setCommSendRef('');
        }}
        title="Send to Customer"
        size="md"
      >
        <div className="space-y-4">
          <FormField label="Quotation Reference" htmlFor="comm-send-ref" required>
            <Input
              id="comm-send-ref"
              value={commSendRef}
              onChange={(e) => setCommSendRef(e.target.value)}
              placeholder="e.g. QT-2026-001"
              maxLength={255}
              disabled={commSendMutation.isPending}
            />
          </FormField>
          <FormField label="Commercial Summary" htmlFor="comm-send-summary" required>
            <Textarea
              id="comm-send-summary"
              value={commSendSummary}
              onChange={(e) => setCommSendSummary(e.target.value)}
              placeholder="Summarise the commercial proposal for the customer…"
              rows={5}
              maxLength={5000}
              disabled={commSendMutation.isPending}
            />
          </FormField>
          {/* D-129 — the quotation PDF is required before Send is enabled */}
          <FormField label="Quotation Document (PDF)" htmlFor="comm-send-doc" required>
            <input
              ref={quotationDocInputRef}
              id="comm-send-doc"
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              onChange={(e) => {
                handleQuotationDocPicked(e.target.files?.[0] ?? null);
                e.target.value = '';
              }}
            />
            {inspection.quotationDocumentName ? (
              <div
                className="flex items-center justify-between gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2"
                data-testid="quotation-doc-attached"
              >
                <span className="flex min-w-0 items-center gap-2 text-sm text-foreground">
                  <FontAwesomeIcon icon={faFilePdf} className="h-4 w-4 shrink-0 text-destructive" />
                  <span className="truncate">{inspection.quotationDocumentName}</span>
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => quotationDocInputRef.current?.click()}
                    disabled={attachQuotationDocMutation.isPending || commSendMutation.isPending}
                  >
                    Replace
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => removeQuotationDocMutation.mutate()}
                    disabled={removeQuotationDocMutation.isPending || commSendMutation.isPending}
                  >
                    {removeQuotationDocMutation.isPending && (
                      <FontAwesomeIcon icon={faSpinner} className="mr-1.5 h-3 w-3 animate-spin" />
                    )}
                    Remove
                  </Button>
                </span>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => quotationDocInputRef.current?.click()}
                disabled={attachQuotationDocMutation.isPending || commSendMutation.isPending}
                className="flex h-16 w-full flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-border text-xs text-muted-foreground transition hover:border-primary/60 hover:text-foreground disabled:opacity-60"
                data-testid="quotation-doc-upload"
              >
                {attachQuotationDocMutation.isPending ? (
                  <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" />
                ) : (
                  <FontAwesomeIcon icon={faFilePdf} className="h-4 w-4" />
                )}
                <span>Upload quotation PDF · max 25 MB</span>
              </button>
            )}
            <p className="mt-1 text-xs text-muted-foreground">
              The customer downloads exactly this document. It is stored as soon as it is selected
              and can be replaced or removed until you send.
            </p>
          </FormField>
          {!sendGate.allowed && (
            <p className="text-xs font-medium text-amber-700" data-testid="send-blocked-reason">
              {sendGate.reason}
            </p>
          )}
          <div className="flex justify-end gap-3">
            <Button
              variant="ghost"
              onClick={() => {
                setCommSendOpen(false);
                setCommSendSummary('');
                setCommSendRef('');
              }}
              disabled={commSendMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() =>
                commSendMutation.mutate({
                  commercialSummary: commSendSummary,
                  quotationReference: commSendRef,
                })
              }
              disabled={
                !commSendSummary.trim() ||
                !commSendRef.trim() ||
                !sendGate.allowed ||
                attachQuotationDocMutation.isPending ||
                commSendMutation.isPending
              }
              title={sendGate.allowed ? undefined : sendGate.reason}
            >
              {commSendMutation.isPending && (
                <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
              )}
              Send to Customer
            </Button>
          </div>
        </div>
      </Modal>

      {/* Commercial Return */}
      <Modal
        open={commReturnOpen}
        onClose={() => {
          setCommReturnOpen(false);
          setCommReturnReason('');
        }}
        title="Return to Operations"
        size="md"
      >
        <div className="space-y-4">
          <FormField label="Return Reason" htmlFor="comm-return-reason" required>
            <Textarea
              id="comm-return-reason"
              value={commReturnReason}
              onChange={(e) => setCommReturnReason(e.target.value)}
              placeholder="Describe what needs to be revised…"
              rows={4}
              maxLength={2000}
              disabled={commReturnMutation.isPending}
            />
          </FormField>
          <div className="flex justify-end gap-3">
            <Button
              variant="ghost"
              onClick={() => {
                setCommReturnOpen(false);
                setCommReturnReason('');
              }}
              disabled={commReturnMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => commReturnMutation.mutate(commReturnReason)}
              disabled={!commReturnReason.trim() || commReturnMutation.isPending}
            >
              {commReturnMutation.isPending && (
                <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
              )}
              Return
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Ownership Dialogs ─────────────────────────────────────────────── */}
      <TransferDialog
        inspectionId={id!}
        currentOwnerId={inspection.assignedEngineerId ?? ''}
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
      />
      <TakeoverDialog
        inspectionId={id!}
        open={takeoverOpen}
        onClose={() => setTakeoverOpen(false)}
      />
      <ForceTransferDialog
        inspectionId={id!}
        open={forceTransferOpen}
        onClose={() => setForceTransferOpen(false)}
      />

      {/* D-129 — staff-recorded customer approval: Work Order number, description and PDF are required */}
      <Modal
        open={custApproveOpen}
        onClose={resetCustApprove}
        title="Record Customer Approval"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Recording the customer&apos;s approval on their behalf requires their Work Order. All
            three fields are mandatory; the approval is not saved until they are provided.
          </p>
          <FormField label="Work Order Number" htmlFor="cust-approve-wo-number" required>
            <Input
              id="cust-approve-wo-number"
              value={custApproveNumber}
              onChange={(e) => setCustApproveNumber(e.target.value)}
              placeholder="e.g. WO-2026-0042"
              maxLength={255}
              disabled={custApproveMutation.isPending}
            />
          </FormField>
          <FormField label="Work Order Description" htmlFor="cust-approve-wo-description" required>
            <Textarea
              id="cust-approve-wo-description"
              value={custApproveDescription}
              onChange={(e) => setCustApproveDescription(e.target.value)}
              placeholder="Describe the work the customer is authorising…"
              rows={4}
              maxLength={5000}
              disabled={custApproveMutation.isPending}
            />
          </FormField>
          <FormField label="Work Order PDF" htmlFor="cust-approve-wo-file" required>
            <input
              id="cust-approve-wo-file"
              type="file"
              accept="application/pdf,.pdf"
              disabled={custApproveMutation.isPending}
              onChange={(e) => {
                const file = e.target.files?.[0] ?? null;
                if (file && !isPdfFile(file)) {
                  toast.error('Only PDF files are accepted for the Work Order.');
                  setCustApproveFile(null);
                  e.target.value = '';
                  return;
                }
                setCustApproveFile(file);
              }}
              className="block w-full text-sm text-foreground file:mr-3 file:rounded-md file:border-0 file:bg-primary/10 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-primary"
            />
            {custApproveFile && (
              <p className="mt-1 text-xs text-muted-foreground">Selected: {custApproveFile.name}</p>
            )}
          </FormField>
          <div className="flex justify-end gap-3">
            <Button
              variant="ghost"
              onClick={resetCustApprove}
              disabled={custApproveMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() =>
                custApproveFile &&
                custApproveMutation.mutate({
                  workOrderNumber: custApproveNumber.trim(),
                  workOrderDescription: custApproveDescription.trim(),
                  file: custApproveFile,
                })
              }
              disabled={
                !custApproveNumber.trim() ||
                !custApproveDescription.trim() ||
                !custApproveFile ||
                custApproveMutation.isPending
              }
            >
              {custApproveMutation.isPending && (
                <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
              )}
              Approve &amp; Submit
            </Button>
          </div>
        </div>
      </Modal>
      {/* Customer Modification Request */}
      <Modal
        open={custModOpen}
        onClose={() => {
          setCustModOpen(false);
          setCustModReason('');
        }}
        title="Request Modification"
        size="md"
      >
        <div className="space-y-4">
          <FormField label="Modification Reason" htmlFor="cust-mod-reason" required>
            <Textarea
              id="cust-mod-reason"
              value={custModReason}
              onChange={(e) => setCustModReason(e.target.value)}
              placeholder="Describe what changes are requested…"
              rows={5}
              maxLength={5000}
              disabled={custModMutation.isPending}
            />
          </FormField>
          <div className="flex justify-end gap-3">
            <Button
              variant="ghost"
              onClick={() => {
                setCustModOpen(false);
                setCustModReason('');
              }}
              disabled={custModMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => custModMutation.mutate(custModReason)}
              disabled={!custModReason.trim() || custModMutation.isPending}
            >
              {custModMutation.isPending && (
                <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
              )}
              Submit Request
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
